'use client';
import { ReactNode } from 'react';
import cn from 'classnames';

import Button from '@/components/common/Button/Button';
import Text from '@/components/common/Text/Text';

import './ReviewCard.style.scss';

type ReviewCardProps = {
  className?: string;
  title: ReactNode;
  children: ReactNode;
  /** Highlighted band pinned to the card's bottom edge, e.g. a "Due now" total. */
  footer?: ReactNode;
  onEdit?: () => void;
  editLabel?: string;
  testId?: string;
};

/**
 * Chrome for a read-only "review" card: a titled box with an optional Edit
 * action in the header and an optional highlighted footer band. Deliberately
 * has no opinion on row layout — the flows that need this chrome (the signup
 * SPA's `dl` rows, the legacy/ambassador flows' freeform text blocks) don't
 * share a row shape, so that stays with each consumer.
 */
function ReviewCard(props: ReviewCardProps) {
  const {
    className = '',
    title,
    children,
    footer,
    onEdit,
    editLabel = 'Edit',
    testId
  } = props;

  return (
    <section
      className={cn('ReviewCard', className)}
      data-testid={testId}
    >
      <div className='ReviewCard-header'>
        <Text
          as='h3'
          variant='title-xxs-700-upper'
          className='ReviewCard-title'
        >
          {title}
        </Text>

        {onEdit && (
          <Button
            className='ReviewCard-edit cursor'
            variant='underline'
            size='small'
            onClick={onEdit}
          >
            {editLabel}
          </Button>
        )}
      </div>

      {children}

      {footer && <div className='ReviewCard-footer'>{footer}</div>}
    </section>
  );
}

export default ReviewCard;
